import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { OnOffSwitch } from "@/components/OnOffSwitch";
import { api, errMsg } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { Mail } from "lucide-react";
import { dialogText } from "@/components/EmailAnnualDialog";
import { useApp } from "@/context/AppContext";

const EMAILS = [
  { kind: "reset", heading: "Reset password email", note: "Use {name} for the person's name.", parts: [
    ["title", "Title", false], ["greeting", "Greeting ({name} becomes the person's name)", false], ["explanation", "Explanation", true],
    ["button", "Button text (the link stays the same)", false], ["ignore", "\"If you did not ask for this\" line", true], ["footer", "Bottom line", true],
  ] },
  { kind: "annual", heading: "MWJK Annual Summary Report email", note: "Use {year} for the report year, {sender} for who sent it and {expires} for the date the links stop working.", parts: [
    ["subject", "Subject line (off uses the standard subject)", false], ["title", "Title", false], ["greeting", "Greeting sentence", true],
    ["report_line", "Grey report line (a note typed when sending replaces it)", true], ["pdf_button", "Download PDF button text", false],
    ["excel_button", "Download Excel button text", false], ["expiry", "When the download links stop working", true], ["footer", "Bottom line", true],
  ] },
  { kind: "annual_dialog", heading: "\"Email Annual Summary\" pop-up window", note: "Use {year} for the report year and {days} for how many days the links work. In the Last sent line use {last_year}, {date} and {by}.", parts: [
    ["title", "Title", false], ["description", "Description under the title", true], ["to_label", "\"To:\" label", false],
    ["note_placeholder", "Placeholder text inside the note box", true], ["pdf_label", "\"PDF report\" label", false],
    ["excel_label", "\"Excel spreadsheet\" label", false], ["last_sent", "\"Last sent\" line", true], ["send_button", "Send button text", false],
  ] },
];

function DialogPreview({ t }) {
  const { settings } = useApp();
  const to = settings?.annual?.gc_email;
  const w = (k) => dialogText(t, k, "", { year: new Date().getFullYear(), days: 30, last_year: new Date().getFullYear(), date: "Nov 3, 2026", by: "Master Admin" });
  return (
    <div className="space-y-4 rounded-lg border border-slate-200 bg-white p-5 shadow-sm" data-testid="email-text-annual_dialog-preview">
      {w("title") && <p className="text-lg font-semibold">{w("title")}</p>}
      {w("description") && <p className="text-sm text-slate-500">{w("description")}</p>}
      <div className="rounded-lg bg-slate-50 p-3 text-sm"><span className="font-semibold">{w("to_label")}</span> <span data-testid="email-text-annual_dialog-preview-to">{to || "(no General Council office email saved yet)"}</span></div>
      <div className="min-h-[5rem] rounded-md border border-slate-200 p-2 text-sm text-slate-400">{w("note_placeholder")}</div>
      <div className="space-y-2 text-sm">
        <label className="flex items-center gap-2"><Checkbox checked disabled /> {w("pdf_label")}</label>
        <label className="flex items-center gap-2"><Checkbox checked disabled /> {w("excel_label")}</label>
      </div>
      {w("last_sent") && <p className="text-xs text-slate-500">{w("last_sent")}</p>}
      <Button className="w-full" tabIndex={-1}><Mail className="mr-2 h-4 w-4" /> {w("send_button")}</Button>
    </div>
  );
}

function Part({ id, k, label, long, v, set }) {
  const Box = long ? Textarea : Input;
  return (
    <div className="space-y-1 rounded-lg border border-slate-200 p-3" data-testid={`${id}-part-${k}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-slate-900">{label}</span>
        <OnOffSwitch checked={v.on} onChange={(on) => set({ ...v, on })} testid={`${id}-${k}-toggle`} />
      </div>
      <Box value={v.text} onChange={(e) => set({ ...v, text: e.target.value })} maxLength={600} disabled={!v.on} className="bg-white" lang="mh" spellCheck={false} data-testid={`${id}-${k}-input`} />
    </div>
  );
}

function EmailCard({ kind, heading, note, parts }) {
  const id = kind === "reset" ? "email-text" : `email-text-${kind}`;
  const [t, setT] = useState(null);
  const [preview, setPreview] = useState({ subject: "", html: "" });
  useEffect(() => { api.get(`/email-text/${kind}`).then((r) => setT(r.data.texts)).catch((e) => toast.error(errMsg(e))); }, [kind]);
  useEffect(() => {
    if (!t || kind === "annual_dialog") return undefined;
    const tm = setTimeout(() => { api.post(`/email-text/${kind}/preview`, t).then((r) => setPreview(r.data)).catch(() => {}); }, 400);
    return () => clearTimeout(tm);
  }, [t, kind]);
  const save = async () => {
    try { setT((await api.put(`/email-text/${kind}`, t)).data.texts); toast.success("Email text saved"); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!t) return null;
  return (
    <section className="space-y-3 rounded-xl border border-slate-200 bg-white p-5 lg:col-span-2" data-testid={`${id}-card`}>
      <h3 className="font-heading text-lg font-bold">Email Text · {heading}</h3>
      <p className="text-sm text-slate-500">Change the words of each part, or switch a part off to hide it. Marshallese letters like õ, ã, ñ and ō can be typed or pasted. {note} The sender and links stay the same.</p>
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-2">
          {parts.map(([k, label, long]) => <Part key={k} id={id} k={k} label={label} long={long} v={t[k]} set={(v) => setT({ ...t, [k]: v })} />)}
          <Button onClick={save} data-testid={`${id}-save`}>Save email text</Button>
        </div>
        <div className="space-y-1">
          <span className="text-sm font-semibold text-slate-900" data-testid={`${id}-preview-subject`}>Preview{preview.subject ? ` · Subject: ${preview.subject}` : ""}</span>
          {kind === "annual_dialog" ? <DialogPreview t={t} /> : <iframe title={`${heading} preview`} srcDoc={preview.html} sandbox="" className="h-[32rem] w-full rounded-lg border border-slate-200 bg-white" data-testid={`${id}-preview`} />}
        </div>
      </div>
    </section>
  );
}

export function AdminEmailText() {
  return <>{EMAILS.map((e) => <EmailCard key={e.kind} {...e} />)}</>;
}
